import { useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal } from "lucide-react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useProducts } from "@/lib/hooks";
import { catLabel } from "@/lib/api";
import CampaignHero from "@/components/store/CampaignHero";
import ProductCard from "@/components/store/ProductCard";
import ProductDialog from "@/components/store/ProductDialog";
import Filters from "@/components/store/Filters";

const VIEW_TITLES = { kampanya: "Kampanyalı Ürünler", "cok-satan": "Çok Satanlar", toplu: "Çok Al Az Öde", "kargo-dahil": "Kargo Bizden" };

export default function Home() {
  const { data: all = [], isLoading } = useProducts();
  const [params] = useSearchParams();
  const [open, setOpen] = useState(null);
  const [sort, setSort] = useState("yeni");
  const [f, setF] = useState({ brands: [], flavors: [], min: "", max: "" });
  const cat = params.get("cat"), view = params.get("view"), q = params.get("q"), type = params.get("type");

  const scoped = useMemo(() => all.filter((p) => {
    if (cat && p.category !== cat) return false;
    if (type && p.sub_type !== type) return false;
    if (view === "kampanya" && !p.is_campaign) return false;
    if (view === "toplu" && !p.bulk_price) return false;
    if (view === "kargo-dahil" && !p.cargo_included) return false;
    if (q) {
      const s = q.toLocaleLowerCase("tr");
      if (![p.name, p.brand, p.flavor, p.weight].join(" ").toLocaleLowerCase("tr").includes(s)) return false;
    }
    return true;
  }), [all, cat, view, q, type]);

  const list = useMemo(() => {
    const r = scoped.filter((p) =>
      (!f.brands.length || f.brands.includes(p.brand)) &&
      (!f.flavors.length || f.flavors.includes(p.flavor)) &&
      (!f.min || p.sale_price >= Number(f.min)) && (!f.max || p.sale_price <= Number(f.max)));
    const sorters = {
      yeni: () => 0, artan: (a, b) => a.sale_price - b.sale_price, azalan: (a, b) => b.sale_price - a.sale_price,
      indirim: (a, b) => b.discount_pct - a.discount_pct, cok: (a, b) => (b.sold_count || 0) - (a.sold_count || 0),
    };
    return [...r].sort(view === "cok-satan" ? sorters.cok : sorters[sort]);
  }, [scoped, f, sort, view]);

  const title = q ? `"${q}" için sonuçlar` : VIEW_TITLES[view] || (cat ? `${catLabel(cat)} Mamaları` : type || "Tüm Ürünler");
  const filters = <Filters products={scoped} all={all} value={f} onChange={setF} />;

  return (
    <>
      {!cat && !view && !q && !type && <CampaignHero products={all} onOpen={setOpen} />}
      <div className="max-w-7xl mx-auto px-4 lg:px-6 pt-6">
        <div className="text-sm text-slate-500 mb-4"><Link to="/" className="text-slate-800 hover:underline">Ana Sayfa</Link> › {title}</div>
        <div className="flex gap-6">
          <aside className="hidden lg:block w-64 shrink-0">{filters}</aside>
          <section className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap justify-between mb-5">
              <div>
                <h2 className="text-2xl sm:text-3xl font-bold text-[#172554]" data-testid="listing-title">{title}</h2>
                <p className="text-sm text-slate-600">Toplam <b data-testid="listing-count">{list.length}</b> ürün bulunmaktadır.</p>
              </div>
              <div className="flex gap-2">
                <MobileFilters>{filters}</MobileFilters>
                <Select value={sort} onValueChange={setSort}>
                  <SelectTrigger data-testid="sort-select" className="w-44 h-11 bg-white"><SelectValue placeholder="Sıralama" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="yeni">Önerilen</SelectItem>
                    <SelectItem value="artan">Fiyat: Artan</SelectItem>
                    <SelectItem value="azalan">Fiyat: Azalan</SelectItem>
                    <SelectItem value="indirim">En Yüksek İndirim</SelectItem>
                    <SelectItem value="cok">Çok Satanlar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {isLoading ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
                {Array.from({ length: 8 }).map((_, i) => <div key={i} className="h-80 rounded-lg bg-slate-200 animate-pulse" />)}
              </div>
            ) : list.length ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4" data-testid="product-grid">
                {list.map((p, i) => <ProductCard key={p.id} p={p} index={i} onOpen={setOpen} />)}
              </div>
            ) : (
              <div className="bg-white border rounded-lg p-12 text-center text-slate-500" data-testid="listing-empty">Bu seçime uygun ürün bulunamadı.</div>
            )}
          </section>
        </div>
      </div>
      <ProductDialog product={open} onClose={() => setOpen(null)} />
    </>
  );
}

function MobileFilters({ children }) {
  const [o, setO] = useState(false);
  return (
    <>
      <button data-testid="open-filters-button" onClick={() => setO(true)} className="lg:hidden h-11 px-4 rounded-md border bg-white font-semibold text-sm flex items-center gap-2">
        <SlidersHorizontal className="h-4 w-4" /> Filtrele
      </button>
      <Sheet open={o} onOpenChange={setO}>
        <SheetContent side="left" className="w-80 overflow-y-auto">
          <SheetHeader><SheetTitle>Filtreler</SheetTitle><SheetDescription>Ürünleri daraltın</SheetDescription></SheetHeader>
          <div className="mt-4">{children}</div>
        </SheetContent>
      </Sheet>
    </>
  );
}
